<template>
  <a-layout-sider :width="200" class="admin-sider">
    <div class="admin-side">
      <div class="side-logo">
        <img src="@/assets/img/logo2.png" class="logo-img" :class="{ hidden: logoFailed }"
          @error="logoFailed = true" @load="logoFailed = false">
        <span v-if="logoFailed" class="logo-text">管理后台</span>
      </div>
      <a-menu theme="dark" mode="inline" :inline-collapsed="isCollapse" :selected-keys="selectedKeys"
        :open-keys="openKeys" @update:open-keys="handleOpenKeysChange" class="side-menu">
        <a-sub-menu key="1">
          <template #icon>
            <DashboardOutlined />
          </template>
          <template #title>数据中心</template>
          <a-menu-item key="1-1" @click="navigateTo('/admin/')">控制台</a-menu-item>
          <a-menu-item key="1-2" @click="navigateTo('/admin/withdrawal')">提现中心</a-menu-item>
          <a-menu-item key="1-3" @click="navigateTo('/userManage')">用户管理</a-menu-item>
        </a-sub-menu>
        <a-menu-item key="2" @click="navigateTo('/admin/')">
          <template #icon>
            <TeamOutlined />
          </template>
          <span>代理中心</span>
        </a-menu-item>
        <a-menu-item key="3" @click="navigateTo('/admin/')">
          <template #icon>
            <AppstoreOutlined />
          </template>
          <span>插件中心</span>
        </a-menu-item>
      </a-menu>
    </div>
  </a-layout-sider>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, watch } from 'vue';
import axios from 'axios';
import { openLoading, fullscreenLoading } from '@/utils/loadingUtils';
import { message } from 'ant-design-vue';
import { useRouter } from 'vue-router';
import { DashboardOutlined, TeamOutlined, AppstoreOutlined } from '@ant-design/icons-vue';

const baseURL = process.env.VUE_APP_API_BASE_URL;

const pathToKeyMap: Record<string, string> = {
  '/admin/withdrawal': '1-2',
  '/admin/login': '1',
  '/userManage': '1-3',
};

const getSelectedKey = (path: string): string => {
  const cleanPath = path.split('?')[0];
  if (pathToKeyMap[cleanPath]) return pathToKeyMap[cleanPath];
  if (cleanPath === '/admin/' || cleanPath === '/admin') return '1-1';
  return '1-1';
};

const getOpenKey = (path: string): string[] => {
  const key = getSelectedKey(path);
  if (key.startsWith('1-')) return ['1'];
  return [];
};

export default defineComponent({
  name: 'AdminHead',
  components: { DashboardOutlined, TeamOutlined, AppstoreOutlined },
  setup() {
    const router = useRouter();
    const isCollapse = ref(false);
    const logoFailed = ref(false);
    const selectedKeys = ref<string[]>([getSelectedKey(location.pathname)]);
    const openKeys = ref<string[]>(getOpenKey(location.pathname));

    watch(() => location.pathname, (newPath) => {
      selectedKeys.value = [getSelectedKey(newPath)];
      openKeys.value = getOpenKey(newPath);
    }, { immediate: true });

    watch(isCollapse, (newVal) => {
      if (newVal) { openKeys.value = []; }
      else { openKeys.value = getOpenKey(location.pathname); }
    });

    const navigateTo = (path: string) => { router.push(path); };
    const handleOpenKeysChange = (keys: string[]) => { openKeys.value = keys; };

    const isLogin = () => {
      openLoading();
      axios.get(baseURL + '/adminApi/isLogin', {
        timeout: 5000,
        headers: { token: localStorage.getItem('token') },
      }).then((res) => {
        fullscreenLoading.close();
        if (res.data.code == 401) {
          message.error('您没有登录');
          setTimeout(() => { location.href = '/admin/login'; }, 1000);
        }
      }).catch((err) => {
        fullscreenLoading.close();
        message.error('请求失败：' + err.message);
      });
    };

    onMounted(isLogin);

    return { isCollapse, logoFailed, selectedKeys, openKeys, navigateTo, handleOpenKeysChange };
  },
});
</script>

<style scoped lang="scss">
.admin-sider {
  background: #001529 !important;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  flex-shrink: 0;
}

.admin-side {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #001529;
}

.side-logo {
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.03);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  gap: 10px;

  .logo-img {
    height: 24px;
    width: auto;
    transition: all 0.2s;

    &.hidden {
      display: none;
    }
  }

  .logo-text {
    font-size: 16px;
    font-weight: 600;
    letter-spacing: 1px;
    background: linear-gradient(135deg, #fff 0%, #a6adf9 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
}

.side-menu {
  flex: 1;
  border-right: none !important;
  background: #001529 !important;
  padding: 8px 0;
  // overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.2) transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  :deep(.ant-menu-item),
  :deep(.ant-menu-submenu-title) {
    margin: 4px 8px !important;
    border-radius: 6px !important;
    height: 40px;
    line-height: 40px;
    font-size: 14px;
    color: rgba(255, 255, 255, 0.75);
    transition: all 0.2s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.08) !important;
      color: #fff !important;
    }
  }

  :deep(.ant-menu-item-selected) {
    background: #1677ff !important;
    color: #fff !important;
    box-shadow: 0 2px 8px rgba(22, 119, 255, 0.4);
  }

  :deep(.ant-menu-submenu-open .ant-menu-submenu-title) {
    background: rgba(255, 255, 255, 0.06) !important;
    color: #fff !important;
  }
}
</style>
